<template>
  <content-container
    description="View plan level data and expand to include fund level or compliance level data."
    class="data-plans"
  >
    <template v-slot:action>
      <mds-search-field
        v-model="searchedPlanId"
        label="Search by Plan ID"
        placeholder="Search by Plan ID"
        @keyup.enter.native="handleSearch"
        @mds-search-field-input-cleared="handleSearch"
      />
    </template>
    <fade-transition>
      <mds-loader v-if="filtersLoading" />
      <div
        v-else
        class="data-plans__content"
      >
        <filter-bar
          ref="data-plans-filters"
          class="data-plans__filters"
          @clear="clearFilters"
        >
          <mds-select
            v-model="selectedFiduciary"
            :options="filterFiduciaryOptions"
            label="Fiduciary:"
          />
          <mds-select
            v-model="selectedProvider"
            :options="filterProviderOptions"
            label="Provider:"
          />
          <mds-select
            v-model="selectedProgram"
            :options="filterProgramOptions"
            label="Program:"
          />
          <mds-select
            v-model="selectedProduct"
            :options="filterProductOptions"
            label="Product:"
          />
          <mds-select
            v-model="selectedMenu"
            :options="filterMenuOptions"
            label="Menu:"
          />
          <mds-select
            v-model="selectedStatus"
            :options="filterStatusOptions"
            label="Plan Status:"
          />
          <mds-date-picker
            v-model="selectedEndDate"
            label="End Date:"
            format="m/d/Y"
          />
          <mds-date-picker
            v-model="selectedUpdateRange"
            label="Updated Range:"
            format="m/d/Y"
            max-date="today"
            mode="range"
          />
        </filter-bar>
        <div class="data-plans__actions">
          <mds-form class="data-plans__actions-toggles">
            <mds-fieldset
              variation="switch-group"
              legend="Display Additional Data"
              hidden-legend
              horizontal
            >
              <mds-switch v-model="displayFundLevelData">
                Fund Level
              </mds-switch>
              <mds-switch v-model="displayComplianceLevelData">
                Compliance Level
              </mds-switch>
            </mds-fieldset>
          </mds-form>
          <mds-button-container right-aligned>
            <mds-button
              id="additional-data-points-action"
              icon-right="caret-down"
              variation="flat"
              @click="displayAdditionalDataPointsPopover = !displayAdditionalDataPointsPopover"
            >
              Additional Data Points
            </mds-button>
            <mds-popover
              v-model="displayAdditionalDataPointsPopover"
              :class="additionalDataPointsModifier"
              :width="additionalDataPointsPopoverWidth"
              position="bottom-left"
              title="Choose additional data points to display"
              title-hidden
              triggered-by="additional-data-points-action"
              class="data-plans__additional-data-points"
            >
              <mds-form>
                <mds-fieldset variation="checkbox-group">
                  <mds-checkbox
                    v-for="option in filteredAdditionalDataPoints"
                    :key="option.key"
                    v-model="option.selected"
                  >
                    {{ option.name }}
                  </mds-checkbox>
                </mds-fieldset>
              </mds-form>
            </mds-popover>
            <mds-button
              icon="download"
              variation="flat"
              @click="downloadPlansData"
            >
              Download
            </mds-button>
          </mds-button-container>
        </div>
        <fade-transition>
          <mds-loader v-if="plansLoading" />
          <!-- Include some error state -->
          <lazy-table
            v-else-if="!plansLoading && plans.length > 0 && !errorLoadingData"
            :key="tableKey"
            :offset="500"
            :header-configs="tableHeaders"
            :row-data="tableData"
            :responsive="true"
            :pagination-config="tablePaginationConfig"
            pagination="below"
            class="data-plans__table"
            @mds-data-table-page-change="handleTablePageChange"
          />
          <mds-empty-state
            v-else
            title="No Plans Found"
            message="There are no plans that meet your search criteria."
            size="large"
            class="data-plans__empty"
          >
            <template v-slot:mds-empty-state-icon>
              <img :src="`${publicPath}images/icons/empty-search.svg`">
            </template>
            <template v-slot:mds-empty-state-actions>
              <mds-button
                variation="primary"
                type="button"
                @click="clearFilters"
              >
                Search Again
              </mds-button>
            </template>
          </mds-empty-state>
        </fade-transition>
      </div>
    </fade-transition>
  </content-container>
</template>

<script>
import ContentContainer from '../../components/ContentContainer/ContentContainer'
import FadeTransition from '../../components/Transitions/FadeTransition'
import LazyTable from '../../components/LazyTable/LazyTable'
import FilterBar from '../../components/FilterBar/FilterBar'
import { MdsButtonContainer, MdsButton } from '@mds/button'
import MdsCheckbox from '@mds/checkbox'
import MdsEmptyState from '@mds/empty-state'
import MdsFieldset from '@mds/fieldset'
import MdsForm from '@mds/form'
import MdsLoader from '@mds/loader'
import MdsPopover from '@mds/popover'
import MdsSearchField from '@mds/search-field'
import MdsSelect from '@mds/select'
import MdsSwitch from '@mds/switch'
import MdsDatePicker from '../../components/Form/MdsDatePicker/MdsDatePicker'
import sessionStorageMixin from '../../mixins/session-storage'
import { formatDateFromIsoString, formatIsoFromDateString, generateRandomId } from '../../../shared/utils'
import { DATE_FORMAT } from '../../../shared/constants/dates'
import { CACHE_FIRST } from '../../graqhql/fetch-policies'
import {
  GET_FIDUCIARIES,
  GET_PROGRAMS,
  GET_PROVIDERS,
  GET_PRODUCTS,
  GET_MENUS,
  GET_PLAN_STATUSES,
  GET_PLANS,
  GET_PLANS_COUNT,
} from '../../graqhql/queries'

const ColumnDisplayStates = {
  ALWAYS: 'ALWAYS', // A required column that should always be displayed.
  BY_DEFAULT: 'BY_DEFAULT', // A column that is initially displayed but can then be turned off.
  NOT_BY_DEFAULT: 'NOT_BY_DEFAULT', // A column that is not initially display but can then be turned on.
}

const PLAN_TABLE_COLUMN_CONFIG = [
  {
    id: 'planId',
    name: 'Plan ID',
    width: '100px',
    display: ColumnDisplayStates.ALWAYS,
  },
  {
    id: 'planProgram',
    name: 'Program',
    width: '100px',
    display: ColumnDisplayStates.ALWAYS,
  },
  {
    id: 'planProduct',
    name: 'Product',
    width: '250px',
    display: ColumnDisplayStates.ALWAYS,
  },
  {
    id: 'planMenu',
    name: 'Menu',
    width: '100px',
    display: ColumnDisplayStates.ALWAYS,
  },
  {
    id: 'planProvider',
    name: 'Provider',
    width: '100px',
    display: ColumnDisplayStates.ALWAYS,
  },
  {
    id: 'planFiduciary',
    name: 'Fiduciary',
    width: '100px',
    display: ColumnDisplayStates.ALWAYS,
  },
  {
    id: 'planStartDate',
    name: 'Start Date',
    width: '100px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'planEndDate',
    name: 'End Date',
    width: '100px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'planStatus',
    name: 'Plan Status',
    width: '100px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'planProcessedDate',
    name: 'Processed Date',
    width: '150px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'planFundedDate',
    name: 'Funded Date',
    width: '100px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
]

const FUND_TABLE_COLUMN_CONFIG = [
  {
    id: 'fundCode',
    name: 'Client Fund Code',
    width: '150px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'fundName',
    name: 'Fund Name',
    width: '200px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'fundSecId',
    name: 'SecID',
    width: '100px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'fundTicker',
    name: 'Ticker',
    width: '100px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'fundCusip',
    name: 'CUSIP',
    width: '100px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'fundId',
    name: 'FundID',
    width: '100px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'fundDataStatus',
    name: 'Data Status',
    width: '100px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'fundMorningstarCategory',
    name: 'Morningstar Category',
    width: '150px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'fundStatus',
    name: 'Fund Status',
    width: '100px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'fundFiduciaryCategory',
    name: 'Fiduciary Category',
    width: '150px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'fundSeriesId',
    name: 'Series ID',
    width: '150px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'fundIsUniversePreferred',
    name: 'Is Universe Preferred',
    width: '150px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'fundClientDisplayName',
    name: 'Client Display Name',
    width: '150px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'fundPendingStatus',
    name: 'Pending Fund Status',
    width: '150px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'fundPendingFiduciaryCategory',
    name: 'Pending Fiduciary Category',
    width: '200px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'fundPendingSeriesId',
    name: 'Pending Series ID',
    width: '150px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
]

const COMPLIANCE_TABLE_COLUMN_CONFIG = [
  {
    id: 'complianceFundCode',
    name: 'Client Fund Code',
    width: '150px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'complianceFirstSuccessDate',
    name: 'First Success Date',
    width: '150px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'complianceTerminationDate',
    name: 'Termination Date',
    width: '150px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
  {
    id: 'complianceDeadlineDate',
    name: 'Deadline Date',
    width: '150px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'complianceErrorCode',
    name: 'Error Code',
    width: '150px',
    display: ColumnDisplayStates.BY_DEFAULT,
  },
  {
    id: 'complianceErrorMessage',
    name: 'Error Message',
    width: '400px',
    display: ColumnDisplayStates.NOT_BY_DEFAULT,
  },
]

const initializeAdditionalDataPointConfig = (config) => {
  return config
    .filter(({ display }) => display !== ColumnDisplayStates.ALWAYS)
    .map(({ id, name, display }) => ({ id, name, selected: display === ColumnDisplayStates.BY_DEFAULT }))
}

// Filter/search constants
const INITIAL_SELECTED_FIDUCIARY = 'Morningstar'
const INITIAL_SELECTED_PROVIDER = ''
const INITIAL_SELECTED_PROGRAM = ''
const INITIAL_SELECTED_PRODUCT = ''
const INITIAL_SELECTED_MENU = ''
const INITIAL_SELECTED_STATUS = ''
const INITIAL_SELECTED_END_DATE = ''
const INITIAL_SELECTED_UPDATED_RANGE = []
const INITIAL_SEARCHED_PLAN_ID = ''

// Pagination constants
const NUMBER_OF_ROWS_PER_PAGE_OPTIONS = [10, 15, 20]
const NUMBER_OF_ROWS_PER_PAGE_DEFAULT = 10

// CSS custom property constants
const BACKGROUND_COLOR_VAR = 'var(--color--row-background-secondary)'

// Enums
const DataStates = {
  PLAN: 'PLAN',
  FUND: 'FUND',
  COMPLIANCE: 'COMPLIANCE',
}

export default {
  components: {
    ContentContainer,
    FadeTransition,
    LazyTable,
    FilterBar,
    MdsButtonContainer,
    MdsButton,
    MdsCheckbox,
    MdsEmptyState,
    MdsFieldset,
    MdsForm,
    MdsLoader,
    MdsPopover,
    MdsSearchField,
    MdsSelect,
    MdsSwitch,
    MdsDatePicker,
  },
  mixins: [
    sessionStorageMixin({
      properties: [
        'selectedFiduciary',
        'selectedProvider',
        'selectedProgram',
        'selectedProduct',
        'selectedMenu',
        'selectedStatus',
        'selectedEndDate',
        'selectedUpdateRange',
      ],
      suffix: 'dataPlans',
    }),
  ],
  apollo: {
    fiduciaries: GET_FIDUCIARIES,
    providers: GET_PROVIDERS,
    programs: GET_PROGRAMS,
    products: GET_PRODUCTS,
    menus: GET_MENUS,
    planStatuses: GET_PLAN_STATUSES,
    plansData: {
      query: GET_PLANS,
      variables () {
        return {
          filters: this.plansFilters,
          options: this.plansOptions,
          pagination: this.plansPagination,
        }
      },
      update: data => data.plans,
      result () {
        /**
         * The BE API endpoint for plan data is paginated which means we only request a "slice" of data at a time.
         * As a small performance improvement, we can load sibling pages behind-the-scences each time we navigate
         * to a new page and store them in the cache. This allows for the user to navigate between pages without
         * ever seeing a loader. Fortunately, Apollo handles whether or not it should grab data from the cache or
         * go to the network to fetch data that hasn't already been cached.
         *
         * The data properties "plansDataNextPage" and "plansDataPrevPage" below aren't actually read from. They
         * are only used so Apollo can set the data somewhere.
         */
        this.fetchAdditonalPlansDataPages()
      },
      error () {
        this.errorLoadingData = true
        // I don't know why this fires THREE times 😔
        // Should hopefully be resolved in the next version of Vue Apollo.
        // Temporary. Still need to define how we handle errors on the UI.
        this.$notifications.error({ text: 'There was an issue fetching plans. Please try again.', tinted: true, persistent: true })
      },
      fetchPolicy: CACHE_FIRST,
    },
    plansCountData: {
      query: GET_PLANS_COUNT,
      variables () {
        return {
          filters: this.plansFilters,
        }
      },
      update: data => data.plansCount,
      error () {
        this.errorLoadingData = true
        // I don't know why this fires THREE times 😔
        // Should hopefully be resolved in the next version of Vue Apollo.
        // Temporary. Still need to define how we handle errors on the UI.
        this.$notifications.error({ text: 'There was an issue fetching plans. Please try again.', tinted: true, persistent: true })
      },
      fetchPolicy: CACHE_FIRST,
    },
  },
  data () {
    return {
      publicPath: process.env.BASE_URL,
      // Initialize Apollo properites
      fiduciaries: [],
      providers: [],
      programs: [],
      products: [],
      menus: [],
      planStatuses: [],
      plansData: { plans: [] },
      plansDataNextPage: null, // Property not actually read from. Apollo needs a property to set query result to.
      plansDataPrevPage: null, // Property not actually read from. Apollo needs a property to set query result to.
      plansCountData: { count: 0 },
      // End initialize Apollo properties
      selectedFiduciary: INITIAL_SELECTED_FIDUCIARY,
      selectedProvider: INITIAL_SELECTED_PROVIDER,
      selectedProgram: INITIAL_SELECTED_PROGRAM,
      selectedProduct: INITIAL_SELECTED_PRODUCT,
      selectedMenu: INITIAL_SELECTED_MENU,
      selectedStatus: INITIAL_SELECTED_STATUS,
      selectedEndDate: INITIAL_SELECTED_END_DATE,
      selectedUpdateRange: INITIAL_SELECTED_UPDATED_RANGE,
      selectedUpdateRangeFilter: INITIAL_SELECTED_UPDATED_RANGE,
      selectedPlanId: INITIAL_SEARCHED_PLAN_ID, // Property that is actually passed to API. Should only be set when user hits "enter" in input.
      searchedPlanId: INITIAL_SEARCHED_PLAN_ID, // Property used by search input's v-model. Should always reflect current value of input.
      tableDataPaginationData: {
        pageSize: NUMBER_OF_ROWS_PER_PAGE_DEFAULT,
        page: 1,
        firstItem: 1,
      },
      displayFundLevelData: false,
      displayComplianceLevelData: false,
      displayAdditionalDataPointsPopover: false,
      additionalDataPoints: {
        [DataStates.PLAN]: initializeAdditionalDataPointConfig(PLAN_TABLE_COLUMN_CONFIG),
        [DataStates.FUND]: initializeAdditionalDataPointConfig(FUND_TABLE_COLUMN_CONFIG),
        [DataStates.COMPLIANCE]: initializeAdditionalDataPointConfig(COMPLIANCE_TABLE_COLUMN_CONFIG),
      },
      errorLoadingData: false,
    }
  },
  computed: {
    filtersLoading () {
      return this.$apollo.queries.fiduciaries.loading ||
        this.$apollo.queries.providers.loading ||
        this.$apollo.queries.programs.loading ||
        this.$apollo.queries.products.loading ||
        this.$apollo.queries.menus.loading ||
        this.$apollo.queries.planStatuses.loading
    },
    plansLoading () {
      return this.$apollo.queries.plansData.loading || this.$apollo.queries.plansCountData.loading
    },
    filterFiduciaryOptions () {
      return this.fiduciaries.map(fiduciary => ({ text: fiduciary.name, value: fiduciary.code }))
    },
    filterProviderOptions () {
      return [
        { text: 'All' },
        ...this.providers.map(provider => ({ text: provider.code, value: provider.code })),
      ]
    },
    filterProgramOptions () {
      return [
        { text: 'All' },
        ...this.programs.map(program => ({ text: program.code, value: program.code })),
      ]
    },
    filterProductOptions () {
      return [
        { text: 'All' },
        ...this.products.map(product => ({ text: product.code, value: product.code })),
      ]
    },
    filterMenuOptions () {
      return [
        { text: 'All' },
        ...this.menus.map(menu => ({ text: menu.name, value: menu.name })),
      ]
    },
    filterStatusOptions () {
      return [
        { text: 'All' },
        ...this.planStatuses.map(status => ({ text: status.name, value: status.name })),
      ]
    },
    plansFilters () {
      const [start, end] = this.selectedUpdateRangeFilter
      return {
        ...(this.selectedPlanId ? { planCode: this.selectedPlanId } : {}),
        ...(this.selectedFiduciary ? { fiduciaryCode: this.selectedFiduciary } : {}),
        ...(this.selectedProvider ? { providerCode: this.selectedProvider } : {}),
        ...(this.selectedProgram ? { programCode: this.selectedProgram } : {}),
        ...(this.selectedProduct ? { productCode: this.selectedProduct } : {}),
        ...(this.selectedMenu ? { menuName: this.selectedMenu } : {}),
        ...(this.selectedStatus ? { planStatus: this.selectedStatus } : {}),
        ...(this.selectedEndDate ? { endDate: formatIsoFromDateString(this.selectedEndDate, DATE_FORMAT) } : {}),
        ...(start && end ? { updatedRangeStart: formatIsoFromDateString(start, DATE_FORMAT), updatedRangeEnd: formatIsoFromDateString(end, DATE_FORMAT) } : {}),
      }
    },
    plansOptions () {
      return {
        includeFundLevel: this.displayFundLevelData,
        includeComplianceLevel: this.displayComplianceLevelData,
      }
    },
    plansPagination () {
      return {
        page: this.tableDataPaginationData.page,
        limit: this.tableDataPaginationData.pageSize,
      }
    },
    additionalDataPointsPopoverWidth () {
      return this.displayFundLevelData ? '500px' : '200px'
    },
    additionalDataPointsModifier () {
      return this.displayFundLevelData ? 'data-plans__additional-data-points--multiple-columns' : null
    },
    filteredAdditionalDataPoints () {
      const types = [
        DataStates.PLAN,
        ...(this.displayFundLevelData ? [DataStates.FUND] : []),
        ...(this.displayComplianceLevelData ? [DataStates.COMPLIANCE] : []),
      ]
      return Object.entries(this.additionalDataPoints).reduce((options, [key, value]) => {
        if (types.includes(key)) {
          return [ ...options, ...value ]
        } else {
          return options
        }
      }, [])
    },
    filteredAdditionalDataPointValues () {
      return this.filteredAdditionalDataPoints
        .filter(datapoint => datapoint.selected)
        .map(datapoint => datapoint.id)
    },
    totalNumberOfPlans () {
      return this.plansCountData?.count
    },
    plans () {
      return this.plansData?.plans
    },
    tableKey () {
      // Force rerender of table when toggling between plan, compliance, and fund data.
      // This is more performant than Vue trying to reuse existing elements
      return `table-fund-${this.displayFundLevelData}-compliance-${this.displayComplianceLevelData}`
    },
    tableHeaders () {
      const headersConfig = [
        ...PLAN_TABLE_COLUMN_CONFIG,
        ...FUND_TABLE_COLUMN_CONFIG,
        ...COMPLIANCE_TABLE_COLUMN_CONFIG,
      ]
      return headersConfig
        .filter(({ id, display }) => display === ColumnDisplayStates.ALWAYS || this.filteredAdditionalDataPointValues.includes(id))
        .map(({ id, name, width }) => ({ fieldName: id, text: name, width }))
    },
    tableData () {
      return this.plans.reduce((rows, plan, planIndex) => {
        const { funds = [], complianceErrors = [] } = plan
        const planRow = this._constructPlanDataRow(plan, planIndex)
        const fundRows = (funds.length && this.displayFundLevelData)
          ? funds.map(fund => this._constructFundDataRow(fund, plan.planId, planIndex))
          : []
        const complianceRows = (complianceErrors.length && this.displayComplianceLevelData)
          ? complianceErrors.map(result => this._constructComplianceDataRow(result, plan.planId, planIndex))
          : []
        return [
          ...rows,
          planRow,
          ...fundRows,
          ...complianceRows,
        ]
      }, [])
    },
    tablePaginationConfig () {
      return {
        pageSizes: NUMBER_OF_ROWS_PER_PAGE_OPTIONS,
        pageSize: this.tableDataPaginationData.pageSize || NUMBER_OF_ROWS_PER_PAGE_DEFAULT,
        page: this.tableDataPaginationData.page,
        totalItems: this.totalNumberOfPlans,
        showItemsInfo: true,
        showItemsSelect: true,
      }
    },
    mappedFiduciaries () {
      return this._constructCodeToNameMap(this.fiduciaries)
    },
  },
  watch: {
    plansFilters () {
      // Any changes to the filters should navigate to page 1
      this.tableDataPaginationData.page = 1
      this.tableDataPaginationData.firstItem = 1
    },
    selectedUpdateRange (newValue, oldValue) {
      // Only want to trigger a new API request if the new selected range contains both or neither start and end dates
      if (newValue.length !== 1) {
        this.selectedUpdateRangeFilter = newValue
      } else {
        this.selectedUpdateRangeFilter = oldValue
      }
    },
    displayFundLevelData (newValue) {
      // Fund level and compliance level data cannot be viewed at the same time
      if (newValue && this.displayComplianceLevelData) {
        this.displayComplianceLevelData = false
      }
    },
    displayComplianceLevelData (newValue) {
      // Fund level and compliance level data cannot be viewed at the same time
      if (newValue && this.displayFundLevelData) {
        this.displayFundLevelData = false
      }
    },
  },
  methods: {
    fetchAdditonalPlansDataPages () {
      // Abstract "sibling" page fetching logic to mixin/hook if needed elsewhere
      const query = (property, options) => this.$apollo.addSmartQuery(property, options)
      const options = (page) => ({
        query: GET_PLANS,
        variables: {
          filters: this.plansFilters,
          options: this.plansOptions,
          pagination: { ...this.plansPagination, page },
        },
        update: data => data.plans,
        fetchPolicy: CACHE_FIRST,
      })

      // Fetch the previous and next page of data
      const { page: currentPage } = this.plansPagination
      query('plansDataNextPage', options(currentPage + 1))

      if (currentPage > 1) {
        query('plansDataPrevPage', options(currentPage - 1))
      }
    },
    async downloadPlansData () {
      this.$notifications.informational({ title: 'Your download has been initiated!', text: 'This could take a few seconds.' })

      try {
        const config = {
          params: { ...this.plansFilters, ...this.plansOptions },
          responseType: 'blob',
          withCredentials: true, // Pass cookies
        }
        const response = await this.$fetch.get(`${process.env.VUE_APP_API_URL}/download/plans`, { config })
        const filename = response.headers['content-disposition'].split('filename=')[1]
        this.$file.downloadBlobFile(filename, response.data)
        this.$notifications.success({ text: 'Your download has completed!', tinted: true })
      } catch (err) {
        this.$notifications.error({ text: 'There was an issue downloading the plans data. Please try again.', tinted: true, persistent: true })
      }
    },
    clearFilters () {
      this.selectedFiduciary = INITIAL_SELECTED_FIDUCIARY
      this.selectedProvider = INITIAL_SELECTED_PROVIDER
      this.selectedProgram = INITIAL_SELECTED_PROGRAM
      this.selectedProduct = INITIAL_SELECTED_PRODUCT
      this.selectedMenu = INITIAL_SELECTED_MENU
      this.selectedStatus = INITIAL_SELECTED_STATUS
      this.selectedEndDate = INITIAL_SELECTED_END_DATE
      this.selectedUpdateRange = INITIAL_SELECTED_UPDATED_RANGE
      this.selectedPlanId = INITIAL_SEARCHED_PLAN_ID
      this.searchedPlanId = INITIAL_SEARCHED_PLAN_ID
    },
    handleSearch () {
      this.selectedPlanId = this.searchedPlanId
    },
    handleTablePageChange (data) {
      this.tableDataPaginationData = data
      // Scroll to "top" of page
      this.$refs['data-plans-filters'].$el.scrollIntoView()
    },
    _constructPlanDataRow (plan, planIndex) {
      return {
        id: `plan-${plan.planId}-${generateRandomId()}`,
        planId: plan.planCode,
        planProgram: plan.programCode || '—',
        planProduct: plan.productCode || '—',
        planMenu: plan.menuName || '—',
        planProvider: plan.providerCode || '—',
        planFiduciary: this.mappedFiduciaries[plan.fiduciaryCode] || '—',
        planEndDate: plan.endDate ? formatDateFromIsoString(plan.endDate, DATE_FORMAT) : '—',
        planStartDate: plan.startDate ? formatDateFromIsoString(plan.startDate, DATE_FORMAT) : '—',
        planStatus: plan.status || '—',
        planProcessedDate: plan.processedDate ? formatDateFromIsoString(plan.processedDate, DATE_FORMAT) : '—',
        planFundedDate: plan.fundedDate ? formatDateFromIsoString(plan.fundedDate, DATE_FORMAT) : '—',
        class: 'data-plans__table-row data-plans__table-row--primary',
        // Custom "zebraing" so all rows of the same plan are grouped together
        ...(planIndex % 2 ? { style: { backgroundColor: BACKGROUND_COLOR_VAR } } : {}),
      }
    },
    _constructFundDataRow (fund, planId, planIndex) {
      return {
        id: `plan-${planId}-fund-${fund.fundId}-${generateRandomId()}`,
        fundCode: fund.clientFundCode || '—',
        fundName: fund.fundName || '—',
        fundSecId: fund.secId || '—',
        fundTicker: fund.ticker || '—',
        fundCusip: fund.cusip || '—',
        fundId: fund.fundId,
        fundDataStatus: fund.dataStatus || '—',
        fundMorningstarCategory: fund.morningstarCategory || '—',
        fundStatus: fund.fundStatus || '—',
        fundFiduciaryCategory: fund.fiduciaryCategory || '—',
        fundSeriesId: fund.seriesId || '—',
        fundIsUniversePreferred: fund.isUniversePreferred ?? '—',
        fundClientDisplayName: fund.clientDisplayName || '—',
        fundPendingStatus: fund.pendingFundStatus || '—',
        fundPendingFiduciaryCategory: fund.pendingFiduciaryCategory || '—',
        fundPendingSeriesId: fund.pendingSeriesId || '—',
        class: 'data-plans__table-row data-plans__table-row--secondary',
        // Custom "zebraing" so all rows of the same plan are grouped together
        ...(planIndex % 2 ? { style: { backgroundColor: BACKGROUND_COLOR_VAR } } : {}),
      }
    },
    _constructComplianceDataRow (complianceError, planId, planIndex) {
      return {
        id: `plan-${planId}-error-${complianceError.ruleId}-${generateRandomId()}`,
        complianceFundCode: complianceError.clientFundCode || '—',
        complianceFirstSuccessDate: complianceError.firstSuccessDate ? formatDateFromIsoString(complianceError.firstSuccessDate, DATE_FORMAT) : '—',
        complianceTerminationDate: complianceError.terminationDate ? formatDateFromIsoString(complianceError.terminationDate, DATE_FORMAT) : '—',
        complianceDeadlineDate: complianceError.deadlineDate ? formatDateFromIsoString(complianceError.deadlineDate, DATE_FORMAT) : '—',
        complianceErrorCode: complianceError.ruleId || '—',
        complianceErrorMessage: complianceError.errorMessage || '—',
        class: 'data-plans__table-row data-plans__table-row--secondary',
        // Custom "zebraing" so all rows of the same plan are grouped together
        ...(planIndex % 2 ? { style: { backgroundColor: BACKGROUND_COLOR_VAR } } : {}),
      }
    },
    _constructCodeToNameMap (array) {
      return array.reduce((map, item) => {
        map[item.code] = item.name
        return map
      }, {})
    },
  },
}
</script>

<style lang="scss" scoped>
.data-plans {
  --color--row-background-secondary: #{$mds-background-color-light-gray};
}

.data-plans__filters {
  margin-bottom: $mds-space-4-x;
}

.data-plans__actions {
  display: flex;
  align-items: center;
  margin-bottom: $mds-space-4-x;
}

.data-plans__actions-toggles {
  flex: 1;
}

.data-plans__content {
  display: flex;
  flex-direction: column;
}

.data-plans__empty {
  align-self: center;
}
</style>

<style lang="scss" module>
// MDS overrides
:global(.data-plans__filters) {
  .mds-label {
    flex: 1 0 0; // Ensure all filters are the same width
  }
}

:global(.data-plans__additional-data-points--multiple-columns) {
  .mds-fieldset {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    grid-template-rows: repeat(11, 1fr); // Multiple columns with a max of 11 items per column
  }
}

:global(.data-plans__table-row) {
  &--secondary {
    .mds-data-table__cell {
      border-top: none; // Remove borders from "fund" and "compliance" table rows
    }
  }
}

:global(.data-plans__empty) {
  .mds-empty-state__icon > * {
    max-width: 100%;
  }
}
</style>
